type LogoProps = {
  variant?: 'full' | 'icon' | 'text';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const sizes = {
  sm: { icon: 24, height: 24 },
  md: { icon: 32, height: 32 },
  lg: { icon: 48, height: 48 },
};

// Icon component - the pink/purple gradient square with stylized "C"
function LogoIcon({ size = 32 }: { size?: number }) {
  const scale = size / 60; // Original icon is 60x60
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="60" height="60" rx={8.14 * scale} fill="url(#logo-gradient)" />
      <path
        d="M42.8254 50.2284C38.1991 52.1127 32.8046 51.9436 28.3099 49.7397C28.2886 49.7303 28.2674 49.7444 28.2674 49.7679V51.1447C28.2674 51.3421 28.0976 51.4878 27.9279 51.4361C23.7812 50.1908 20.2966 46.7886 17.8943 42.8038C17.8773 42.7756 17.8391 42.7897 17.8391 42.8226V46.2435C17.8391 46.4926 17.5802 46.6336 17.4019 46.4785C8.66289 38.7484 8.66289 21.6483 17.4019 13.9229C17.576 13.7678 17.8391 13.9135 17.8391 14.1578V17.0572C17.8391 17.0901 17.8773 17.1042 17.8943 17.076C20.2923 12.9878 23.7939 9.89103 27.9109 8.36381C28.0849 8.29803 28.2674 8.4484 28.2674 8.65046V9.89573C28.2674 9.91923 28.2886 9.93332 28.3099 9.92393C32.7664 7.73883 38.0845 7.55556 42.6854 9.37883C43.1565 9.56679 43.3942 10.1495 43.1947 10.657C42.3671 12.7716 41.1871 15.7743 40.351 17.889C40.1642 18.3589 39.6804 18.5703 39.2475 18.3871C35.5464 16.8129 31.1875 18.3683 28.747 21.8268C28.59 22.0524 28.2632 21.9349 28.2632 21.6483V18.6408C28.2632 18.6126 28.2377 18.5985 28.2165 18.6126C21.7184 23.246 21.7184 36.4411 28.2165 41.0745C28.2377 41.0886 28.2632 41.0745 28.2632 41.0463V38.0435C28.2632 37.7569 28.5857 37.6394 28.747 37.865C31.2512 41.4175 35.8266 42.9353 39.5785 41.1591C40.0284 40.9429 40.5505 41.1685 40.733 41.676C41.4842 43.7859 42.5962 46.9249 43.3305 48.9878C43.5088 49.4859 43.2796 50.0498 42.8254 50.2331V50.2284Z"
        fill="white"
      />
      <defs>
        <linearGradient
          id="logo-gradient"
          x1="63.49"
          y1="154.47"
          x2="-4.16"
          y2="153.50"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFCAF0" />
          <stop offset="1" stopColor="#B957FF" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Text component - "Content4Life" text
function LogoText({ height = 32 }: { height?: number }) {
  const scale = height / 60;
  const textWidth = 234 * scale; // Original text portion width
  return (
    <svg
      width={textWidth}
      height={height}
      viewBox="0 0 234 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M33.5337 33.7328H36.487C35.2109 39.1654 30.6168 43.0667 24.5279 43.0667C17.0899 43.0667 12.0948 37.3424 12.0948 30.4149C12.0948 23.5238 17.0899 17.7266 24.5279 17.7266C30.5439 17.7266 35.1015 21.5914 36.4506 26.9147H33.4972C32.3305 23.1957 28.9032 20.4611 24.5644 20.4611C18.8765 20.4611 14.9023 25.0552 14.9023 30.4149C14.9023 35.8475 18.8765 40.3686 24.5644 40.3686C29.122 40.3686 32.4763 37.4883 33.5337 33.7328ZM48.0592 43.1032C42.2983 43.1032 38.3972 38.8373 38.3972 33.6234C38.3972 28.3731 42.2983 24.1801 48.0592 24.1801C53.7105 24.1801 57.5751 28.3731 57.5751 33.6234C57.5751 38.8373 53.7105 43.1032 48.0592 43.1032ZM48.0592 40.478C52.1065 40.478 54.8411 37.3424 54.8411 33.6234C54.8411 29.868 52.1065 26.8053 48.0592 26.8053C43.9024 26.8053 41.1678 29.868 41.1678 33.6234C41.1678 37.3789 43.9024 40.478 48.0592 40.478ZM69.9112 24.1801C74.3961 24.1801 77.4588 27.2793 77.4588 32.165V42.6292H74.7242V32.639C74.7242 29.2117 72.6824 26.8417 69.4013 26.8417C65.7918 26.8417 63.2029 29.7221 63.2029 33.6599V42.6292H60.4683V24.6541H63.1664V27.7897H63.2394C64.6249 25.6021 67.0314 24.1801 69.9112 24.1801ZM93.7732 24.6541V27.2793H85.2052V37.9622C85.2052 39.2384 85.8979 39.9676 87.2104 39.9676H93.7732V42.6292H86.7364C83.9653 42.6292 82.4704 41.1343 82.4704 38.3998V27.2793H79.1894V24.6541H82.4704V18.2006H85.2052V24.6541H93.7732ZM103.957 24.1801C109.645 24.1801 113.437 28.6283 113.109 34.6079H97.5402C97.7591 37.8893 100.421 40.478 104.103 40.478C106.801 40.478 109.025 39.129 109.937 37.0507H112.781C111.578 40.7697 108.187 43.1032 103.994 43.1032C98.3061 43.1032 94.6601 38.8008 94.6601 33.6599C94.6601 28.446 98.4521 24.1801 103.957 24.1801ZM103.884 26.8053C100.494 26.8053 98.0144 29.1023 97.6131 32.165H110.192C109.718 29.0294 107.275 26.8053 103.884 26.8053ZM125.496 24.1801C129.981 24.1801 133.044 27.2793 133.044 32.165V42.6292H130.309V32.639C130.309 29.2117 128.267 26.8417 124.986 26.8417C121.377 26.8417 118.788 29.7221 118.788 33.6599V42.6292H116.053V24.6541H118.751V27.7897H118.824C120.21 25.6021 122.616 24.1801 125.496 24.1801ZM149.359 24.6541V27.2793H140.791V37.9622C140.791 39.2384 141.484 39.9676 142.796 39.9676H149.359V42.6292H142.322C139.551 42.6292 138.056 41.1343 138.056 38.3998V27.2793H134.775V24.6541H138.056V18.2006H140.791V24.6541H149.359ZM163.535 42.6292V37.7799H150.846V34.2797L162.878 17.1067H167.873V33.9516H171.337V37.7799H167.873V42.6292H163.535ZM155.55 33.9516H163.79V21.9195L155.55 33.9516ZM173.881 42.6292V18.2006H178.256V38.3633H191.346V42.6292H173.881ZM193.025 16.3775H197.218V21.9195H193.025V16.3775ZM193.025 24.5083H197.218V42.6292H193.025V24.5083ZM213.743 20.4247H207.545C206.487 20.4247 205.904 21.008 205.904 22.0289V24.5083H213.743V28.5919H205.904V42.6292H201.711V28.5919H198.794V24.5083H201.711V21.3362C201.711 18.3829 203.753 16.3775 206.706 16.3775H213.743V20.4247ZM222.526 23.9978C228.542 23.9978 232.334 28.7742 231.787 35.0089H217.458C217.786 37.3424 219.755 39.2019 222.599 39.2748C224.641 39.3113 226.428 38.3998 227.157 36.9413H231.496C230.329 40.8791 226.792 43.1397 222.526 43.1397C216.583 43.1397 213.119 38.7279 213.119 33.587C213.119 28.2272 216.948 23.9978 222.526 23.9978ZM222.453 27.8626C219.938 27.8626 218.042 29.3575 217.531 31.5816H227.448C226.902 29.3211 225.006 27.8626 222.453 27.8626Z"
        fill="#202020"
      />
    </svg>
  );
}

export function Logo({ variant = 'full', size = 'md', className = '' }: LogoProps) {
  const { icon: iconSize, height } = sizes[size];

  if (variant === 'icon') {
    return (
      <div className={className}>
        <LogoIcon size={iconSize} />
      </div>
    );
  }

  if (variant === 'text') {
    return (
      <div className={className}>
        <LogoText height={height} />
      </div>
    );
  }

  // Full variant: icon + text
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LogoIcon size={iconSize} />
      <LogoText height={height} />
    </div>
  );
}
